import { useCallback, useEffect, useRef, useState } from 'react';

import { devLog } from '../../../../../utils';

interface StreamRetryResult {
  retryState: RetryState;
  increaseRetryCount: () => void;
  resetRetryCount: () => void;
  cleanupTimers: () => void;
  isRetryScheduled: () => boolean;
}

interface RetryState {
  count: number;
  urlHash: string;
}

/**
 * Hook to manage retry logic for stream loading with delay and reset timers.
 */
export function useStreamRetry(): StreamRetryResult {
  const [retryState, setRetryState] = useState<RetryState>({
    count: 0,
    urlHash: '',
  });

  const isRetryScheduledRef = useRef(false);

  /** Timer to reset the retry count after 60 seconds without errors */
  const retryResetTimerRef = useRef<number | null>(null);
  /** Timer to delay retries by 5 seconds */
  const retryDelayTimerRef = useRef<number | null>(null);

  const cleanupTimers = useCallback(() => {
    if (retryResetTimerRef.current) {
      window.clearTimeout(retryResetTimerRef.current);
      retryResetTimerRef.current = null;
    }
    if (retryDelayTimerRef.current) {
      window.clearTimeout(retryDelayTimerRef.current);
      retryDelayTimerRef.current = null;
      isRetryScheduledRef.current = false;
    }
  }, []);

  const scheduleRetryReset = useCallback(() => {
    if (retryResetTimerRef.current) {
      window.clearTimeout(retryResetTimerRef.current);
    }
    retryResetTimerRef.current = window.setTimeout(() => {
      devLog('Resetting retry count to 0 after 60 seconds');
      setRetryState((prev) => ({ count: 0, urlHash: prev.urlHash }));
      retryResetTimerRef.current = null;
    }, 60000);
  }, []);

  const increaseRetryCount = useCallback(() => {
    if (!retryDelayTimerRef.current) {
      isRetryScheduledRef.current = true;
      devLog('Increase retry count in 5s');

      retryDelayTimerRef.current = window.setTimeout(() => {
        setRetryState((prev) => {
          // Generate a new hash that is different from the previous one
          let newHash = Math.random().toString(36).substring(2, 8);
          while (newHash === prev.urlHash) {
            newHash = Math.random().toString(36).substring(2, 8);
          }
          return {
            count: prev.count + 1,
            urlHash: newHash,
          };
        });

        scheduleRetryReset();
        retryDelayTimerRef.current = null;
        isRetryScheduledRef.current = false;
      }, 5000);
    }
  }, [scheduleRetryReset]);

  const resetRetryCount = useCallback(() => {
    setRetryState((prev) => ({ count: 0, urlHash: prev.urlHash }));
    cleanupTimers();
  }, [cleanupTimers]);

  // Cleanup timers on component unmount
  useEffect(() => {
    return cleanupTimers;
  }, [cleanupTimers]);

  return {
    retryState,
    increaseRetryCount,
    resetRetryCount,
    cleanupTimers,
    isRetryScheduled: () => isRetryScheduledRef.current,
  };
}
